<template>
  <div class="quiz-container">
    <div class="quiz-progress">
      <div
        class="progress-dot"
        v-for="s in totalSteps"
        :key="s"
        :class="{ active: s === step, completed: s < step }"
      ></div>
    </div>

    <!-- Step 1: Name + Type -->
    <div v-if="step === 1" class="quiz-step">
      <h2>Who is this person?</h2>
      <div class="field-group">
        <label for="personName">Their name</label>
        <input
          id="personName"
          type="text"
          v-model="name"
          placeholder="e.g. Sarah Chen"
          class="text-input"
        />
      </div>
      <div class="field-group">
        <label>How do you know them?</label>
        <div class="picker-grid">
          <button
            v-for="t in typeOptions"
            :key="t.value"
            class="picker-button"
            :class="{ selected: type === t.value }"
            @click="type = t.value"
          >
            {{ t.label }}
          </button>
        </div>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="$emit('cancel')">Cancel</button>
        <button class="button primary" :disabled="!name || !type" @click="step = 2">Next</button>
      </div>
    </div>

    <!-- Step 2: Vibe Picker -->
    <div v-if="step === 2" class="quiz-step">
      <h2>What's your vibe together?</h2>
      <p class="step-hint">Pick 1 to 3</p>
      <div class="chip-grid">
        <button
          v-for="v in vibeOptions"
          :key="v"
          class="chip-button"
          :class="{ selected: vibe.includes(v) }"
          @click="toggleVibe(v)"
        >
          {{ v }}
        </button>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 1">Back</button>
        <button class="button primary" :disabled="vibe.length === 0" @click="step = 3">Next</button>
      </div>
    </div>

    <!-- Step 3: Closeness -->
    <div v-if="step === 3" class="quiz-step">
      <h2>How close are you two right now?</h2>
      <div class="picker-grid">
        <button
          v-for="c in closenessOptions"
          :key="c"
          class="picker-button wide"
          :class="{ selected: closeness === c }"
          @click="closeness = c"
        >
          {{ c }}
        </button>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 2">Back</button>
        <button class="button primary" :disabled="!closeness" @click="step = 4">Next</button>
      </div>
    </div>

    <!-- Step 4: Hangout Style -->
    <div v-if="step === 4" class="quiz-step">
      <h2>What's a thing you two actually do?</h2>
      <p class="step-hint">Pick up to 2</p>
      <div class="picker-grid">
        <button
          v-for="h in hangoutOptions"
          :key="h"
          class="picker-button wide"
          :class="{ selected: hangoutStyle.includes(h) }"
          @click="toggleHangout(h)"
        >
          {{ h }}
        </button>
      </div>
      <div class="field-group">
        <input
          type="text"
          v-model="hangoutOther"
          maxlength="120"
          placeholder="or the real answer:"
          class="date-input"
          style="width: 100%;"
        />
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 3">Back</button>
        <button class="button primary" :disabled="hangoutStyle.length === 0 && !hangoutOther.trim()" @click="step = 5">Next</button>
      </div>
    </div>

    <!-- Step 5: Communication Style + latest topic -->
    <div v-if="step === 5" class="quiz-step">
      <h2>How do you usually talk?</h2>
      <p class="step-hint">Optional</p>
      <div class="picker-grid">
        <button
          v-for="c in commOptions"
          :key="c"
          class="picker-button wide"
          :class="{ selected: communicationStyle === c }"
          @click="communicationStyle = communicationStyle === c ? '' : c"
        >
          {{ c }}
        </button>
      </div>
      <p class="step-hint">What did you two last talk about?</p>
      <div class="field-group">
        <input
          type="text"
          v-model="lastTalked"
          maxlength="300"
          placeholder="e.g. her chaotic kitchen reno, the playoffs, nothing and everything"
          class="date-input"
          style="width: 100%;"
        />
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 4">Back</button>
        <button class="button primary" @click="step = 6">Next</button>
      </div>
    </div>

    <!-- Step 6: Done or Go Deeper -->
    <div v-if="step === 6" class="quiz-step">
      <h2>That's the basics!</h2>
      <p class="step-hint">You can save now or add more details to get better postcards.</p>
      <div class="step-actions done-actions">
        <button class="button primary" @click="save" :disabled="saving">That's enough!</button>
        <button class="button secondary" @click="step = 7">Tell me more</button>
      </div>
    </div>

    <!-- Step 7: Extended Details -->
    <div v-if="step === 7" class="quiz-step">
      <h2>The deeper stuff</h2>

      <div class="field-group">
        <label for="nickname">What do you call them?</label>
        <input
          id="nickname"
          type="text"
          v-model="nickname"
          maxlength="40"
          placeholder="e.g. Mimi, Coach, dork"
          class="text-input"
        />
      </div>

      <div class="field-group">
        <label>Their pronouns</label>
        <div class="chip-grid">
          <button
            v-for="p in pronounOptions"
            :key="p"
            class="chip-button"
            :class="{ selected: pronouns === p }"
            @click="pronouns = pronouns === p ? '' : p"
          >
            {{ p }}
          </button>
        </div>
      </div>

      <div class="field-group">
        <label for="interests">Interests (comma-separated)</label>
        <input
          id="interests"
          type="text"
          v-model="interestsRaw"
          placeholder="e.g. hiking, board games, sci-fi"
          class="text-input"
        />
      </div>

      <div class="field-group">
        <label for="insideJokes">Inside jokes</label>
        <input
          id="insideJokes"
          type="text"
          v-model="insideJokesRaw"
          placeholder="e.g. the lake incident, don't mention pigeons"
          class="text-input"
        />
      </div>

      <div class="field-group">
        <label for="avoid">Topics to avoid</label>
        <input
          id="avoid"
          type="text"
          v-model="avoidRaw"
          placeholder="e.g. work stress, ex partners"
          class="text-input"
        />
      </div>

      <div class="field-group">
        <label>Important dates</label>
        <div class="dates-list" v-if="significantDates.length">
          <div class="date-chip" v-for="(sd, idx) in significantDates" :key="idx">
            <span>{{ sd.label }} — {{ formatSignificantDate(sd) }}</span>
            <button class="remove-btn" @click="removeSignificantDate(idx)">&times;</button>
          </div>
        </div>
        <div class="date-add-form">
          <select v-model="newDateType" class="text-input date-select">
            <option value="">Type...</option>
            <option value="birthday">Birthday</option>
            <option value="anniversary">Anniversary</option>
            <option value="custom">Custom</option>
          </select>
          <input
            v-if="newDateType === 'custom'"
            type="text"
            v-model="newDateLabel"
            placeholder="Label (e.g. Friendiversary)"
            class="text-input date-label-input"
          />
          <select v-model="newDateMonth" class="text-input date-select">
            <option :value="0">Month...</option>
            <option v-for="m in 12" :key="m" :value="m">{{ monthName(m) }}</option>
          </select>
          <input
            type="number"
            v-model.number="newDateDay"
            placeholder="Day"
            min="1"
            max="31"
            class="text-input date-day-input"
          />
          <input
            type="number"
            v-model.number="newDateYear"
            placeholder="Year (optional)"
            min="1900"
            max="2100"
            class="text-input date-year-input"
          />
          <button class="button secondary date-add-btn" :disabled="!canAddDate" @click="addSignificantDate">Add</button>
        </div>
      </div>

      <div class="field-group">
        <label for="notes">Any other notes</label>
        <textarea
          id="notes"
          v-model="notes"
          placeholder="Anything else that would help us write great postcards"
          class="text-input"
          rows="3"
        ></textarea>
      </div>

      <div class="field-group">
        <label>How often should we remind you to send?</label>
        <div class="picker-grid">
          <button
            v-for="f in frequencyOptions"
            :key="f.value"
            class="picker-button"
            :class="{ selected: frequency === f.value }"
            @click="frequency = f.value"
          >
            {{ f.label }}
          </button>
        </div>
      </div>

      <div class="address-section">
        <h3>Their mailing address</h3>
        <div class="field-group">
          <label>Address</label>
          <SimpleGoogleAutocomplete
            ref="addressAutocomplete"
            @update:modelValue="handleAddressUpdate"
          />
        </div>
        <div class="field-group">
          <label for="optional">Apt / Suite (optional)</label>
          <input id="optional" type="text" v-model="address.optional" class="text-input" />
        </div>
      </div>

      <div class="prefs-section">
        <h3>Auto-send preferences</h3>
        <p class="step-hint">
          Let the assistant reach out to {{ name || "them" }} on your behalf — only when you opt in.
        </p>
        <div class="field-group">
          <div class="picker-grid">
            <button
              v-for="c in channelOptions"
              :key="c.value"
              class="picker-button"
              :class="{ selected: preferredChannel === c.value }"
              @click="preferredChannel = c.value"
            >
              {{ c.label }}
            </button>
          </div>
        </div>

        <div v-if="preferredChannel === 'email'" class="field-group">
          <label for="recipientEmail">Their email</label>
          <input
            id="recipientEmail"
            type="email"
            v-model="email"
            placeholder="e.g. sarah@example.com"
            class="text-input"
          />
          <label class="consent-check">
            <input type="checkbox" v-model="emailConsent" />
            <span>I have {{ name || "their" }}'s permission to email them on my behalf.</span>
          </label>
        </div>

        <div v-if="preferredChannel === 'physical'" class="field-group">
          <label class="consent-check">
            <input type="checkbox" v-model="postalConsent" />
            <span>I have permission to mail to the address above.</span>
          </label>
          <p v-if="!hasAddress" class="prefs-warning">
            Add a mailing address above to enable physical sends.
          </p>
        </div>
      </div>

      <div class="step-actions">
        <button class="button secondary" @click="step = 6">Back</button>
        <button class="button primary" @click="save" :disabled="saving">Save</button>
      </div>
    </div>

    <Spinner :show="saving" message="Saving..." />
  </div>
</template>

<script>
import Spinner from "@/components/SpinnerComp.vue";
import { AddressAutocomplete as SimpleGoogleAutocomplete } from "@thinking-of-u/ui";
import { db, auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { monthName, formatSignificantDate } from "@thinking-of-u/shared";

export default {
  name: "AddPersonQuiz",
  components: { Spinner, SimpleGoogleAutocomplete },
  props: {
    editPerson: {
      type: Object,
      default: null,
    },
  },
  emits: ["complete", "cancel"],
  data() {
    return {
      user: null,
      authUnsubscribe: null,
      step: 1,
      saving: false,
      name: "",
      type: "",
      vibe: [],
      closeness: "",
      hangoutStyle: [],
      hangoutOther: "",
      communicationStyle: "",
      lastTalked: "",
      nickname: "",
      pronouns: "",
      interestsRaw: "",
      insideJokesRaw: "",
      avoidRaw: "",
      notes: "",
      frequency: "whenever",
      // Auto-send preferences. Default fail-closed ("off"): the assistant never
      // reaches out on the user's behalf until they explicitly opt a recipient
      // into a channel and confirm consent.
      preferredChannel: "off",
      email: "",
      emailConsent: false,
      postalConsent: false,
      significantDates: [],
      newDateType: "",
      newDateMonth: 0,
      newDateDay: null,
      newDateYear: null,
      newDateLabel: "",
      address: {
        street: "",
        optional: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      },
      typeOptions: [
        { value: "friend", label: "Friend" },
        { value: "family", label: "Family" },
        { value: "partner", label: "Partner" },
        { value: "coworker", label: "Coworker" },
        { value: "mentor", label: "Mentor" },
        { value: "other", label: "Other" },
      ],
      vibeOptions: [
        "silly", "deep", "chaotic", "warm", "sarcastic", "chill",
        "weird", "supportive", "competitive", "nostalgic", "creative", "dry",
      ],
      closenessOptions: [
        "basically family",
        "close",
        "catching back up",
        "drifting, want to fix that",
        "new-ish",
      ],
      pronounOptions: ["she/her", "he/him", "they/them", "other"],
      hangoutOptions: [
        "Going on adventures",
        "Eating & drinking",
        "Making stuff",
        "Talking forever",
        "Being competitive",
        "Doing nothing",
        "Getting into trouble",
        "Supporting each other",
      ],
      commOptions: [
        "Memes & links",
        "Long voice notes",
        "Quick texts",
        "Phone calls",
        "In person only",
        "You don't that's the point",
      ],
      frequencyOptions: [
        { value: "weekly", label: "Weekly" },
        { value: "biweekly", label: "Every 2 weeks" },
        { value: "monthly", label: "Monthly" },
        { value: "quarterly", label: "Every few months" },
        { value: "whenever", label: "Whenever" },
      ],
      channelOptions: [
        { value: "off", label: "Don't auto-send" },
        { value: "email", label: "Email" },
        { value: "physical", label: "Physical mail" },
      ],
    };
  },
  computed: {
    totalSteps() {
      // All reachable steps, including the optional deep step — the old
      // hardcoded 5 left step 6 with no progress dot at all.
      return 7;
    },
    hasAddress() {
      return !!(this.address.street && this.address.city && this.address.postalCode);
    },
    canAddDate() {
      if (!this.newDateType || !this.newDateMonth || !this.newDateDay) return false;
      if (this.newDateType === "custom" && !this.newDateLabel.trim()) return false;
      return true;
    },
  },
  created() {
    // Store the unsubscribe so the listener is torn down on unmount — this
    // modal genuinely remounts, so without teardown the listeners accumulate.
    this.authUnsubscribe = onAuthStateChanged(auth, (user) => {
      this.user = user || null;
    });
    if (this.editPerson) {
      this.populateFromEdit();
      this.$nextTick(() => {
        if (this.address.street && this.$refs.addressAutocomplete) {
          const displayAddr = [this.address.street, this.address.city, this.address.state, this.address.postalCode, this.address.country].filter(Boolean).join(", ");
          this.$refs.addressAutocomplete.setQuery(displayAddr);
        }
      });
    }
  },
  beforeUnmount() {
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = null;
    }
  },
  methods: {
    toggleVibe(v) {
      const idx = this.vibe.indexOf(v);
      if (idx > -1) {
        this.vibe.splice(idx, 1);
      } else if (this.vibe.length < 3) {
        this.vibe.push(v);
      }
    },
    toggleHangout(h) {
      const idx = this.hangoutStyle.indexOf(h);
      if (idx > -1) {
        this.hangoutStyle.splice(idx, 1);
      } else if (this.hangoutStyle.length < 2) {
        this.hangoutStyle.push(h);
      }
    },
    populateFromEdit() {
      const p = this.editPerson;
      this.name = p.name || "";
      this.type = p.type || "";
      this.vibe = p.vibe ? [...p.vibe] : [];
      this.closeness = p.closeness || "";
      // Older docs stored hangoutStyle as a single string — migrate on edit.
      this.hangoutStyle = Array.isArray(p.hangoutStyle) ?
        [...p.hangoutStyle] :
        (p.hangoutStyle ? [p.hangoutStyle] : []);
      this.hangoutOther = "";
      this.communicationStyle = p.communicationStyle || "";
      this.nickname = p.nickname || "";
      this.pronouns = p.pronouns || "";
      // lastTalked is deliberately NOT prefilled — it's an ephemeral "what's
      // new" capture, not a stored profile field.
      this.interestsRaw = (p.interests || []).join(", ");
      this.insideJokesRaw = (p.insideJokes || []).join(", ");
      this.avoidRaw = (p.avoid || []).join(", ");
      this.notes = p.notes || "";
      this.frequency = p.frequency || "whenever";
      this.significantDates = p.significantDates ? [...p.significantDates] : [];
      this.address = p.address
        ? { ...p.address }
        : { street: "", optional: "", city: "", state: "", postalCode: "", country: "" };
      this.preferredChannel = p.preferredChannel || "off";
      this.email = p.email || "";
      this.emailConsent = p.consent === true || !!p.consentedAt;
      this.postalConsent = p.postalConsent === true || !!p.postalConsentedAt;
    },
    addSignificantDate() {
      const labels = { birthday: "Birthday", anniversary: "Anniversary" };
      const label = this.newDateType === "custom" ? this.newDateLabel.trim() : labels[this.newDateType];
      this.significantDates.push({
        type: this.newDateType,
        label,
        month: this.newDateMonth,
        day: this.newDateDay,
        year: this.newDateYear || null,
      });
      this.newDateType = "";
      this.newDateMonth = 0;
      this.newDateDay = null;
      this.newDateYear = null;
      this.newDateLabel = "";
    },
    removeSignificantDate(index) {
      this.significantDates.splice(index, 1);
    },
    monthName,
    formatSignificantDate,
    handleAddressUpdate(parsedAddress) {
      if (!parsedAddress) {
        this.address = { ...this.address, street: "", city: "", state: "", postalCode: "", country: "" };
        return;
      }
      this.address = {
        ...this.address,
        street: parsedAddress.street || "",
        city: parsedAddress.city || "",
        state: parsedAddress.state || "",
        postalCode: parsedAddress.postalCode || "",
        country: parsedAddress.country || "",
      };
    },
    parseCommaSeparated(raw) {
      if (!raw || !raw.trim()) return [];
      return raw.split(",").map((s) => s.trim()).filter(Boolean);
    },
    async save() {
      if (!this.user) {
        this.$toast.error("You must be logged in.");
        return;
      }
      this.saving = true;
      try {
        // Channel-scoped consent: only record a consent flag when that channel
        // is actually chosen AND its requirement is met (valid email / a mailing
        // address). The backend agent fails closed, but this keeps us from ever
        // writing a consent the data can't back.
        const email = this.email.trim();
        const emailOptIn = this.preferredChannel === "email" && this.emailConsent && !!email;
        const postalOptIn = this.preferredChannel === "physical" && this.postalConsent && this.hasAddress;
        const now = Date.now();

        // hangoutStyle is an array (up to 2 chips + the free-text "real
        // answer"); backend consumers tolerate the legacy string form.
        const hangout = [...this.hangoutStyle];
        if (this.hangoutOther.trim()) hangout.push(this.hangoutOther.trim());

        // "What did you two last talk about?" seeds the signals stream so the
        // recency engine starts fed on day one.
        const lastTalked = this.lastTalked.trim();
        const priorSignals = (this.editPerson && this.editPerson.signals) || [];
        const signals = lastTalked ?
          [...priorSignals.slice(-19), { text: lastTalked, at: now }] :
          [...priorSignals];

        const docData = {
          userId: this.user.uid,
          name: this.name,
          nickname: this.nickname.trim(),
          type: this.type,
          pronouns: this.pronouns === "other" ? "" : this.pronouns,
          vibe: this.vibe,
          closeness: this.closeness,
          hangoutStyle: hangout,
          communicationStyle: this.communicationStyle,
          signals,
          interests: this.parseCommaSeparated(this.interestsRaw),
          frequency: this.frequency,
          notes: this.notes,
          insideJokes: this.parseCommaSeparated(this.insideJokesRaw),
          avoid: this.parseCommaSeparated(this.avoidRaw),
          significantDates: [...this.significantDates],
          address: { ...this.address },
          hasAddress: this.hasAddress,
          // Auto-send preferences + consent (read by the autonomous agent).
          preferredChannel: this.preferredChannel,
          email,
          consent: emailOptIn,
          consentedAt: emailOptIn ? now : null,
          postalConsent: postalOptIn,
          postalConsentedAt: postalOptIn ? now : null,
          totalSent: 0,
          lastContactAt: null,
          approvedTones: [],
          rejectedTones: [],
          recentStyles: [],
          createdAt: now,
          updatedAt: now,
          status: "active",
        };

        const relationshipsRef = collection(db, "relationships");

        if (this.editPerson && this.editPerson.id) {
          // Preserve existing stats when editing
          const updateData = { ...docData };
          delete updateData.totalSent;
          delete updateData.lastContactAt;
          delete updateData.approvedTones;
          delete updateData.rejectedTones;
          delete updateData.recentStyles;
          delete updateData.createdAt;
          updateData.updatedAt = Date.now();
          await updateDoc(doc(relationshipsRef, this.editPerson.id), updateData);
          this.$emit("complete", { id: this.editPerson.id, ...updateData });
        } else {
          const docRef = await addDoc(relationshipsRef, docData);
          this.$emit("complete", { id: docRef.id, ...docData });
        }
      } catch (error) {
        this.$toast.error("Failed to save. Please try again.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quiz-container {
  max-width: 560px;
  margin: 0 auto;
  padding: 20px;
}

.quiz-progress {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}

.progress-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #e0e0e0;
  transition: background-color 0.3s;
}

.progress-dot.active {
  background-color: var(--hollywood-cerise);
  transform: scale(1.2);
}

.progress-dot.completed {
  background-color: var(--accent2-color);
}

.quiz-step h2 {
  margin: 0 0 8px;
  color: var(--rich-black);
  font-size: 1.4rem;
}

.step-hint {
  margin: 0 0 16px;
  color: var(--violet-lush);
  font-size: 0.9rem;
}

.field-group {
  margin-bottom: 16px;
}

.field-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--rich-black);
}

.text-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.text-input:focus {
  outline: none;
  border-color: var(--hollywood-cerise);
}

.picker-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.picker-button {
  padding: 10px 18px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  color: var(--rich-black);
}

.picker-button:hover {
  border-color: var(--hollywood-cerise);
}

.picker-button.selected {
  border-color: var(--hollywood-cerise);
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  font-weight: 600;
}

.picker-button.wide {
  flex: 1 1 calc(50% - 10px);
  min-width: 140px;
  text-align: center;
}

.chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.chip-button {
  padding: 6px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 20px;
  background-color: #fff;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  color: var(--rich-black);
}

.chip-button:hover {
  border-color: var(--hollywood-cerise);
}

.chip-button.selected {
  border-color: var(--hollywood-cerise);
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  font-weight: 600;
}

.step-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
}

.done-actions {
  flex-direction: column;
  align-items: stretch;
}

.step-actions .button {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.step-actions .button.primary {
  background-color: var(--accent2-color);
  color: white;
}

.step-actions .button.primary:hover {
  background-color: var(--accent-color);
}

.step-actions .button.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.step-actions .button.secondary {
  background-color: var(--hollywood-cerise);
  color: white;
}

.step-actions .button.secondary:hover {
  background-color: #e41393;
}

.dates-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.date-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 0.85rem;
}

.remove-btn {
  background: none;
  border: none;
  color: var(--hollywood-cerise);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
}

.date-add-form {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.date-select {
  flex: 0 0 auto;
  width: auto;
  min-width: 100px;
}

.date-label-input {
  flex: 1 1 120px;
  min-width: 120px;
}

.date-day-input {
  width: 70px;
  flex: 0 0 70px;
}

.date-year-input {
  width: 90px;
  flex: 0 0 90px;
}

.date-add-btn {
  padding: 8px 14px;
  font-size: 0.85rem;
}

.address-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.address-section h3 {
  margin: 0 0 12px;
  color: var(--rich-black);
  font-size: 1.1rem;
}

.prefs-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.prefs-section h3 {
  margin: 0 0 6px;
  color: var(--rich-black);
  font-size: 1.1rem;
}

.consent-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  font-weight: 400;
  font-size: 0.9rem;
  color: var(--rich-black);
  cursor: pointer;
}

.consent-check input[type="checkbox"] {
  margin-top: 2px;
  flex: 0 0 auto;
}

.prefs-warning {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: var(--hollywood-cerise);
}

.address-row {
  display: flex;
  gap: 12px;
}

.address-row .field-group {
  flex: 1;
}

@media (max-width: 768px) {
  .quiz-container {
    padding: 16px;
  }

  .picker-button.wide {
    flex: 1 1 100%;
  }

  .address-row {
    flex-direction: column;
    gap: 0;
  }
}
</style>
